/**
 * Analytics (PostHog, EU) and error tracking (Sentry), both optional and free-tier
 * (docs/architecture.md §14). Nothing loads without the env keys. During the playtest PostHog
 * records without asking (D24); a player can turn it off in Settings → Game stats. Events from
 * the first moments of a visit, before PostHog has loaded, wait in memory.
 *   VITE_POSTHOG_KEY, VITE_POSTHOG_HOST (default: the relay below), VITE_SENTRY_DSN
 *
 * Production builds send both through caged.gg itself: the site Worker relays /r/* to PostHog and
 * /r-e to Sentry (tools/site-worker), so ad blockers that block their own addresses don't hide
 * players from the playtest numbers. Dev builds talk to PostHog directly (no Worker there).
 */
import type { PostHog } from 'posthog-js';

declare const __BUILD_ID__: string;

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const RELAY = import.meta.env.PROD;
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || (RELAY ? '/r' : 'https://eu.i.posthog.com');
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
/** "denied" once the player turned game stats off (also kept from the old consent banner). */
const STATS_KEY = 'caged.consent.v1';
/** Events kept until PostHog has loaded. */
const HELD_MAX = 200;

type Props = Record<string, unknown>;

let posthog: PostHog | null = null;
let sentry: typeof import('@sentry/react') | null = null;
/** Tracked before PostHog loaded, with the time they happened. */
let held: { event: string; props: Props; at: Date }[] = [];
/** Errors caught before Sentry loaded (it loads once the first screen is up: main.tsx). */
let heldErrors: { error: unknown; extra?: Props }[] = [];
/** Sent with every event (language, device, …). */
let context: Props = {};

export const analyticsConfigured = Boolean(POSTHOG_KEY);

/** Game stats are on unless the player turned them off. */
export function statsEnabled(): boolean {
  try {
    return localStorage.getItem(STATS_KEY) !== 'denied';
  } catch {
    return true;
  }
}

const statsListeners = new Set<() => void>();

/** For the Settings switch. */
export function subscribeStats(cb: () => void): () => void {
  statsListeners.add(cb);
  return () => {
    statsListeners.delete(cb);
  };
}

export async function setStatsEnabled(on: boolean): Promise<void> {
  try {
    localStorage.setItem(STATS_KEY, on ? 'granted' : 'denied');
  } catch {
    // ignore
  }
  for (const l of statsListeners) l();
  if (on) {
    await startPosthog();
  } else {
    held = [];
    // Forgets this browser's analytics id, then stops sending (in this order: a reset also
    // clears PostHog's own opt-out).
    posthog?.reset(true);
    posthog?.opt_out_capturing();
  }
}

async function startPosthog(): Promise<void> {
  if (!POSTHOG_KEY) return;
  if (!posthog) {
    const mod = await import('posthog-js');
    mod.default.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Links into PostHog itself (the relay only carries events).
      ui_host: 'https://eu.posthog.com',
      persistence: 'localStorage',
      // Game events only (tracking.ts), plus one pageview per visit for the web overview.
      autocapture: false,
      capture_pageview: true,
      capture_exceptions: false,
      capture_performance: false,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      disable_session_recording: true,
      disable_surveys: true,
      // No feature flags, and no scripts loaded from PostHog.
      advanced_disable_flags: true,
      disable_external_dependency_loading: true,
      // Retention and breakdowns work per player without an account.
      person_profiles: 'always',
    });
    posthog = mod.default;
  }
  // Turned off once, back on now (PostHog remembers the opt-out itself).
  if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
  posthog.register(context);
  const queued = held;
  held = [];
  for (const h of queued) posthog.capture(h.event, h.props, { timestamp: h.at });
}

export async function initPlatform(): Promise<void> {
  if (SENTRY_DSN) {
    sentry = await import('@sentry/react');
    sentry.init({
      dsn: SENTRY_DSN,
      ...(RELAY ? { tunnel: '/r-e' } : {}),
      release: __BUILD_ID__,
      environment: import.meta.env.DEV ? 'development' : 'production',
      tracesSampleRate: 0,
    });
    for (const { error, extra } of heldErrors) reportError(error, extra);
    heldErrors = [];
  }
  if (statsEnabled()) await startPosthog();
}

/** Properties sent with every event from now on (and on the events still held). */
export function setContext(props: Props): void {
  context = { ...context, ...props };
  posthog?.register(props);
}

export function track(event: string, props: Props = {}): void {
  if (!POSTHOG_KEY || !statsEnabled()) return;
  if (posthog) posthog.capture(event, props);
  // PostHog is still loading: keep it (with the context of this moment).
  else if (held.length < HELD_MAX) held.push({ event, props: { ...context, ...props }, at: new Date() });
}

/** An error the app caught itself (the crash screen), for Sentry. */
export function reportError(error: unknown, extra?: Props): void {
  if (sentry) sentry.captureException(error, extra ? { extra } : undefined);
  else if (SENTRY_DSN && heldErrors.length < 20) heldErrors.push({ error, extra });
}
